import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import UndoIcon from "@mui/icons-material/Undo";
import { Box, IconButton, Stack, Tooltip } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import type {
  OnboardingFormValues,
  RestoreDescription,
  SuggestedField,
  SuggestionState,
} from "./mergePrefillIntoFormValues.js";
import { describeRestore } from "./mergePrefillIntoFormValues.js";

/**
 * Colours of a field a website lookup filled. A suggested field keeps the
 * normal field background (user decision 7.10.2026, over the handover's tint):
 * the tag is its only marker.
 */
const RESTORE_COLOR = "#1A4CBF";
const RESTORE_HOVER = "rgba(0, 30, 100, 0.08)";

/** What the form fields know about the lookup that filled them. */
export interface OnboardingSuggestions {
  states: Partial<Record<SuggestedField, SuggestionState>>;
  /** The values before the lookup and the ones it wrote. */
  before: OnboardingFormValues;
  applied: OnboardingFormValues;
  /** Writes the suggestion (or, for `input`, the user's own input) back into the field. */
  onRestore: (field: SuggestedField, kind: RestoreDescription["kind"]) => void;
}

// What a field may carry at its right end: the Autocomplete's clear button,
// an input adornment, a select's arrow.
const END_ADORNMENT_SELECTOR =
  ".MuiAutocomplete-endAdornment, .MuiInputAdornment-positionEnd, .MuiSelect-icon";

const RESTORE_SIZE = 36;
const EDGE_GAP = 6;

/**
 * Where the controls at the field's right end sit, measured from its right edge
 * (`endInset`: the distance from the field's right edge to an end adornment's
 * left edge, 0 without one). With a restore slot the restore button sits at the
 * field's edge (user decision 7.10.2026: the clear button first, the restore
 * button last) and an end adornment moves `adornmentShift` px left of it; the
 * slot is kept while the field is suggested too, so nothing moves once it is
 * edited. `controlsLeft` is the left edge of the leftmost control (0 without
 * any); `inputPaddingRight` keeps the typed text clear of the controls.
 */
export function getSuggestionLayout({
  endInset,
  restoreSlot,
}: {
  endInset: number;
  restoreSlot: boolean;
}): {
  controlsLeft: number;
  restoreRight: number;
  inputPaddingRight: number | null;
  adornmentShift: number;
} {
  if (restoreSlot) {
    const adornmentShift = endInset > 0 ? RESTORE_SIZE : 0;
    const controlsLeft =
      endInset > 0 ? endInset + adornmentShift : EDGE_GAP + RESTORE_SIZE;
    return {
      controlsLeft,
      restoreRight: EDGE_GAP,
      inputPaddingRight: controlsLeft + EDGE_GAP,
      adornmentShift,
    };
  }
  return {
    controlsLeft: endInset,
    restoreRight: EDGE_GAP,
    inputPaddingRight: null,
    adornmentShift: 0,
  };
}

// MUI draws a lifted label at 75 %; its max-width applies before that scale.
const LIFTED_LABEL_SCALE = 0.75;
const LABEL_TAG_GAP = 8;
const LABEL_TRANSITION_EVENTS = [
  "transitionrun",
  "transitionend",
  "transitioncancel",
] as const;
// The label's transitions that move or resize it; its colour also changes on
// focus, which leaves the tag where it is.
const LABEL_GEOMETRY_PROPERTIES = new Set(["transform", "max-width"]);

const isLabelGeometryTransition = (animation: Animation): boolean =>
  animation.playState === "running" &&
  "transitionProperty" in animation &&
  LABEL_GEOMETRY_PROPERTIES.has(String(animation.transitionProperty));

interface FrameMeasurements {
  frameWidth: number;
  /** The distance from the frame's right edge to its field's end adornment, 0 without one. */
  endInset: number;
  /** The tag's own width, 0 without a tag. */
  tagWidth: number;
  /** Where the label starts, from the frame's left edge; `null` without one. */
  labelLeft: number | null;
  /** The right and top of the label's drawn box (after its scale), from the frame's top left. */
  labelRight: number | null;
  labelTop: number;
}

const NO_MEASUREMENTS: FrameMeasurements = {
  frameWidth: 0,
  endInset: 0,
  tagWidth: 0,
  labelLeft: null,
  labelRight: null,
  labelTop: 0,
};

/**
 * Measures the frame's field: its end adornment, the tag's width and where the
 * lifted label starts. Positions that depend on the layout (the tag's) are
 * computed from these, so a measurement never sees a tag that is about to move.
 */
function useFrameMeasurements(
  frame: React.RefObject<HTMLDivElement>,
  tag: React.RefObject<HTMLDivElement>,
  active: boolean,
  layoutKey: string
): FrameMeasurements {
  const [measured, setMeasured] =
    React.useState<FrameMeasurements>(NO_MEASUREMENTS);
  React.useLayoutEffect(() => {
    const element = frame.current;
    if (!active || !element) {
      return;
    }
    const measure = () => {
      const frameRect = element.getBoundingClientRect();
      // The adornments may already be moved left of the restore button; the
      // inset is measured where they sit on their own.
      const appliedShift = Number(element.dataset.adornmentShift ?? 0);
      const adornments = element.querySelectorAll<HTMLElement>(
        END_ADORNMENT_SELECTOR
      );
      const adornmentLeft = Math.min(
        ...Array.from(
          adornments,
          adornment => adornment.getBoundingClientRect().left
        )
      );
      // A label starts at the same place lifted or not, so lifting it on
      // focus or input needs no new measurement of its start. Its end and top
      // do change: MUI lifts it with a transform transition (no
      // ResizeObserver reports one), so while it runs the tag waits hidden
      // and is placed once it ends.
      const label = element.querySelector<HTMLElement>(".MuiInputLabel-root");
      const labelRect = label?.getBoundingClientRect();
      const labelMoving =
        label?.getAnimations?.().some(isLabelGeometryTransition) ?? false;
      const next: FrameMeasurements = {
        frameWidth: Math.round(frameRect.width),
        endInset:
          adornments.length > 0
            ? Math.max(
                0,
                Math.round(frameRect.right - adornmentLeft - appliedShift)
              )
            : 0,
        tagWidth: tag.current
          ? Math.round(tag.current.getBoundingClientRect().width)
          : 0,
        labelLeft: labelRect
          ? Math.round(labelRect.left - frameRect.left)
          : null,
        labelRight:
          labelRect && !labelMoving ? labelRect.right - frameRect.left : null,
        labelTop: labelRect && !labelMoving ? labelRect.top - frameRect.top : 0,
      };
      setMeasured(previous =>
        previous.frameWidth === next.frameWidth &&
        previous.endInset === next.endInset &&
        previous.tagWidth === next.tagWidth &&
        previous.labelLeft === next.labelLeft &&
        previous.labelRight === next.labelRight &&
        previous.labelTop === next.labelTop
          ? previous
          : next
      );
    };
    measure();
    // A web font that loads later changes the label's text width.
    const fonts = typeof document === "undefined" ? undefined : document.fonts;
    fonts?.addEventListener("loadingdone", measure);
    // The label lifts (or drops) with a transition: hidden from its start,
    // placed at its end.
    const label = element.querySelector<HTMLElement>(".MuiInputLabel-root");
    LABEL_TRANSITION_EVENTS.forEach(type =>
      label?.addEventListener(type, measure)
    );
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(measure);
    observer?.observe(element);
    // The tag's width changes with the language and once fonts load; the
    // label's with its cut, which places the tag behind it.
    if (tag.current) {
      observer?.observe(tag.current);
    }
    if (label) {
      observer?.observe(label);
    }
    return () => {
      observer?.disconnect();
      fonts?.removeEventListener("loadingdone", measure);
      LABEL_TRANSITION_EVENTS.forEach(type =>
        label?.removeEventListener(type, measure)
      );
    };
  }, [frame, tag, active, layoutKey]);
  return measured;
}

interface Props {
  /** The lookup field this wraps; without one the frame shows nothing. */
  field?: SuggestedField;
  suggestions?: OnboardingSuggestions;
  children: React.ReactNode;
}

/**
 * Marks one field a website lookup filled. While it holds the lookup's value it
 * is tagged "Suggested" right after its label. Once edited, a restore
 * button inside the field at the right writes the suggestion back (the company
 * name instead offers the user's own input once the lookup replaced it).
 */
export const OnboardingSuggestionFrame = ({
  field,
  suggestions,
  children,
}: Props) => {
  const { t } = useTranslate(["OrganisationProfile"]);
  const state = field ? suggestions?.states[field] : undefined;
  const restore: RestoreDescription | null =
    field && suggestions ? describeRestore(field, state, suggestions) : null;
  const suggested = state === "suggested";
  const frameRef = React.useRef<HTMLDivElement>(null);
  const tagRef = React.useRef<HTMLDivElement>(null);
  const { frameWidth, endInset, tagWidth, labelLeft, labelRight, labelTop } =
    useFrameMeasurements(
      frameRef,
      tagRef,
      suggested || Boolean(restore),
      `${state ?? ""}:${restore?.kind ?? ""}`
    );
  const layout = getSuggestionLayout({
    endInset,
    restoreSlot: suggested || Boolean(restore),
  });
  // How wide the label may be (as drawn) so that it, and the tag behind it,
  // end before the controls at the right; the label gets the ellipsis, the
  // tag stays whole.
  const labelRoom =
    labelLeft !== null && (suggested || restore)
      ? Math.max(
          0,
          frameWidth -
            layout.controlsLeft -
            labelLeft -
            LABEL_TAG_GAP -
            (suggested && tagWidth > 0 ? tagWidth + LABEL_TAG_GAP : 0)
        )
      : null;

  const restoreLabel = restore
    ? restore.kind === "input"
      ? t("Restore your input: {value}", {
          ns: "OrganisationProfile",
          value: restore.value,
        })
      : t("Restore suggestion: {value}", {
          ns: "OrganisationProfile",
          value: restore.value,
        })
    : "";

  return (
    <Box
      ref={frameRef}
      data-adornment-shift={layout.adornmentShift}
      sx={{
        position: "relative",
        ...(layout.adornmentShift > 0 && {
          [`& ${END_ADORNMENT_SELECTOR.split(", ").join(", & ")}`]: {
            // A margin, not a transform: the select arrow turns with one.
            marginRight: `${layout.adornmentShift}px`,
          },
        }),
        // A long label ends (with an ellipsis) before the tag behind it or
        // the controls.
        ...(labelRoom !== null && {
          "& .MuiInputLabel-root": { maxWidth: `${Math.floor(labelRoom)}px` },
          "& .MuiInputLabel-root.MuiInputLabel-shrink": {
            maxWidth: `${Math.floor(labelRoom / LIFTED_LABEL_SCALE)}px`,
          },
        }),
        ...(layout.inputPaddingRight !== null && {
          "& .MuiFilledInput-input": { pr: `${layout.inputPaddingRight}px` },
        }),
      }}
    >
      {children}
      {suggested && (
        <Stack
          direction="row"
          spacing={`${4 / LIFTED_LABEL_SCALE}px`}
          ref={tagRef}
          data-testid="suggestion-tag"
          sx={{
            // On the label's line, right after it, drawn like MUI's lifted
            // label (16px in a 1.4375em line, scaled to 75 % from the top
            // left), so both share one baseline.
            position: "absolute",
            top: labelTop,
            left: (labelRight ?? 12) + LABEL_TAG_GAP,
            height: "1.4375em",
            transform: `scale(${LIFTED_LABEL_SCALE})`,
            transformOrigin: "top left",
            alignItems: "center",
            whiteSpace: "nowrap",
            pointerEvents: "none",
            fontSize: 16,
            lineHeight: "1.4375em",
            fontWeight: 500,
            color: RESTORE_COLOR,
            // Hidden until it knows where the label ends.
            visibility: labelRight === null ? "hidden" : undefined,
          }}
        >
          {/* 14px and 4px apart once drawn at 75 %. */}
          <AutoAwesomeIcon
            aria-hidden
            sx={{ fontSize: 14 / LIFTED_LABEL_SCALE }}
          />
          <span>{t("Suggested", { ns: "OrganisationProfile" })}</span>
        </Stack>
      )}
      {restore && field && suggestions && (
        <Tooltip title={restoreLabel}>
          <IconButton
            aria-label={restoreLabel}
            onClick={() => suggestions.onRestore(field, restore.kind)}
            sx={{
              position: "absolute",
              top: 6,
              right: layout.restoreRight,
              width: RESTORE_SIZE,
              height: RESTORE_SIZE,
              p: 0,
              borderRadius: "4px",
              color: RESTORE_COLOR,
              zIndex: 2,
              "&:hover": { bgcolor: RESTORE_HOVER },
            }}
          >
            <UndoIcon aria-hidden sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};
